import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { driver, type Config, type Driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import "./tour.css";
import { useAuth } from "../AuthContext";
import { useAppState } from "../AppStateContext";
import { useI18n } from "../i18n";
import { demoStore } from "../demo/demoStore";
import { useDemo } from "../demo/useDemo";
import { useDataChangedTick } from "../useDataChanged";
import { TourContext } from "./tourContext";
import { DEMO_CHAPTERS } from "../demo/demoTour";
import { chapterMatches, chapterRoutes, resolveChapter } from "./chapters";
import { revealInScrollers } from "./stepHelpers";
import { isTourSeen, markTourSeen, resetTourSeen } from "./tourState";
import type { StepData, TourChapter, TourContext as TourCtx } from "./types";

const DESKTOP = "(min-width: 861px)";
const WAIT_FOR_MS = 4000;
const POLL_MS = 220;
// The full-screen scan-intro video plays when a scan starts. A tour step must
// not pop over it, so a chapter waits until it's gone — but no longer than this
// cap, in case the overlay never clears (reduced motion, blocked autoplay).
const INTRO_CAP_MS = 6000;

type Ending = "done" | "skipped" | "demo" | "chapter" | null;

/** Poll for the screen's anchor (and for the scan-intro overlay to clear),
 *  then start. Returns the cleanup. */
function whenAnchorReady(
  target: { waitFor: string; delayMs?: number; waitMs?: number },
  start: () => void,
) {
  let cancelled = false;
  let waited = 0;
  let introWaited = 0;
  const waitMs = target.waitMs ?? WAIT_FOR_MS;
  const poll = () => {
    if (cancelled) return;
    const anchorReady = !!document.querySelector(target.waitFor);
    const introUp = !!document.querySelector(".scan-intro");
    if (anchorReady && (!introUp || introWaited >= INTRO_CAP_MS)) {
      window.setTimeout(() => {
        if (!cancelled) start();
      }, target.delayMs ?? 200);
      return;
    }
    if (anchorReady && introUp) introWaited += POLL_MS;
    waited += POLL_MS;
    if (waited < waitMs) window.setTimeout(poll, POLL_MS);
  };
  const t = window.setTimeout(poll, POLL_MS);
  return () => {
    cancelled = true;
    window.clearTimeout(t);
  };
}

export function TourProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { projects } = useAppState();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const demo = useDemo();
  // Re-runs the demo effect when the sample data changes, so a chapter whose
  // state guard stops holding (a project created, a target verified) hands off
  // to the next chapter even though its own last step's element has unmounted.
  const dataTick = useDataChangedTick();
  const pathname = location.pathname;

  const driverRef = useRef<Driver | null>(null);
  const endingRef = useRef<Ending>(null);
  const runningPathRef = useRef<string | null>(null);
  const rewindToRef = useRef<number | null>(null);
  // Set when Settings asks to replay: show the welcome card even with projects.
  const forceWelcomeRef = useRef(false);
  const [tick, setTick] = useState(0);

  const senderName = user?.display_name?.split(" ")[0] || undefined;

  const disposeActive = useCallback(() => {
    const d = driverRef.current;
    if (!d) return;
    d.destroy();
    document.body.classList.remove("ces-tour-action");
    if (driverRef.current === d) driverRef.current = null;
    endingRef.current = null;
    runningPathRef.current = null;
  }, []);

  const endDemo = useCallback(
    (status: "done" | "skipped", to: string) => {
      markTourSeen();
      endingRef.current = status;
      disposeActive();
      demoStore.stop();
      navigate(to);
    },
    [navigate, disposeActive],
  );

  const run = useCallback(
    (chapter: TourChapter, index: number) => {
      const isWelcome = index === 0;
      const routes = chapterRoutes(chapter.route);
      const onSecondScreen = routes.length > 1 && !matchPath(routes[0], location.pathname);
      const canRewind = !isWelcome && (onSecondScreen || index > 1);

      const rewind = () => {
        endingRef.current = "chapter";
        disposeActive();
        if (onSecondScreen) {
          navigate(chapter.resume ?? routes[0]);
          return;
        }
        const prev = DEMO_CHAPTERS[index - 1];
        rewindToRef.current = index - 1;
        navigate(prev.resume ?? chapterRoutes(prev.route)[0]);
      };

      let animationOff = false;
      const jumpNext = (drv: Driver, from: Element) => {
        if (!document.contains(from)) {
          animationOff = true;
          drv.setConfig({ ...config, animate: false });
        }
        drv.moveNext();
      };
      const onSettled = (drv: Driver) => {
        if (animationOff) {
          animationOff = false;
          drv.setConfig(config);
        }
        window.requestAnimationFrame(() => driverRef.current?.refresh());
      };

      /** Next on a live step whose outcome is missing: hold — act, or nudge. */
      const hold = (data: StepData | undefined, drv: Driver): boolean => {
        const live = data?.live;
        if (!live?.advanceWhen || !(live.act || live.nudge) || live.advanceWhen()) return false;
        if (live.act) {
          live.act();
          return true;
        }
        const { text, open } = live.nudge!;
        const popover = drv.getState().popover;
        const desc = popover?.description;
        if (desc) desc.textContent = typeof text === "function" ? text() : text;
        if (data?.nextHidden) popover?.wrapper.classList.add("ces-tour-no-next");
        drv.refresh();
        open?.();
        return true;
      };

      const ctx: TourCtx = { userName: senderName, pathname: location.pathname, t };
      const steps = chapter.steps(ctx).map((s, i): DriveStep => {
        // No per-chapter "2 of 6": it reset every chapter and confused people.
        // The stepper in the demo banner shows overall progress instead.
        const popover = { showProgress: false, ...s.popover };
        const data = s.data as StepData | undefined;
        const live = data?.live;

        // A step that ends with the user clicking something (data.nextHidden)
        // shows no Next — the click itself advances, so a Next button only
        // confuses. Info and scan-advance steps keep Next.
        const actionStep = !!data?.nextHidden;
        if (actionStep) {
          const canBack = i > 0 || canRewind;
          popover.showButtons = canBack ? ["previous"] : [];
          if (i === 0 && canRewind) popover.onPrevClick = () => rewind();
        } else if (i === 0 && !popover.showButtons) {
          if (canRewind) {
            popover.showButtons = ["previous", "next"];
            popover.onPrevClick = () => rewind();
          } else {
            popover.showButtons = ["next"];
          }
        }

        const onHighlightStarted: DriveStep["onHighlightStarted"] = (elm, step, opts) => {
          // Pulse the ring only when the user has to click the target.
          document.body.classList.toggle("ces-tour-action", actionStep);
          if (elm) revealInScrollers(elm, data?.reveal);
          s.onHighlightStarted?.(elm, step, opts);
        };

        if (!live) {
          return { ...s, popover, onHighlightStarted, onHighlighted: (elm, step, opts) => { onSettled(opts.driver); s.onHighlighted?.(elm, step, opts); } };
        }

        let observer: MutationObserver | null = null;
        const stop = () => {
          observer?.disconnect();
          observer = null;
        };
        if (live.advanceWhen && (live.nudge || live.act)) {
          popover.onNextClick = (_elm, _step, opts) => {
            if (!hold(data, opts.driver)) opts.driver.moveNext();
          };
          popover.doneBtnText ??= popover.nextBtnText ?? t("tour.next");
        }
        return {
          ...s,
          popover,
          onHighlightStarted,
          onHighlighted: (elm, step, opts) => {
            onSettled(opts.driver);
            s.onHighlighted?.(elm, step, opts);
            stop();
            if (!elm) return;
            const alreadyDone = live.advanceWhen?.() ?? false;
            const wholeDocument = live.observe === "document";
            observer = new MutationObserver((records) => {
              if (wholeDocument) {
                const ours = records.every((r) => {
                  const node = r.target instanceof Element ? r.target : r.target.parentElement;
                  return node?.closest(".driver-popover, .driver-overlay");
                });
                if (ours) return;
              }
              opts.driver.refresh();
              const done = live.advanceWhen?.() ?? false;
              if (done && !alreadyDone) {
                stop();
                jumpNext(opts.driver, elm);
              } else if (live.onLost === "previous" && !document.contains(elm)) {
                stop();
                opts.driver.movePrevious();
              }
            });
            observer.observe(wholeDocument ? document.body : elm, {
              childList: true,
              subtree: true,
              attributes: true,
            });
          },
          onDeselected: (elm, step, opts) => {
            stop();
            s.onDeselected?.(elm, step, opts);
          },
        };
      });

      endingRef.current = null;

      const advance = (step: DriveStep, drv: Driver) => {
        if (drv.hasNextStep()) {
          drv.moveNext();
          return;
        }
        const data = step.data as StepData | undefined;
        if (hold(data, drv)) return;
        if (data?.startsDemo) {
          endingRef.current = "demo";
          disposeActive();
          demoStore.start();
          return;
        }
        if (data?.finishDemo) {
          endDemo("done", data.navigateTo ?? "/overview");
          return;
        }
        // Chapter over: the next one starts on the route the user navigates to,
        // or right here when nothing moves (chapter 1 follows the card).
        endingRef.current = "chapter";
        disposeActive();
        if (data?.navigateTo) navigate(data.navigateTo);
        else demoStore.advanceChapter();
      };

      const config: Config = {
        steps,
        animate: true,
        smoothScroll: false,
        allowClose: isWelcome,
        overlayColor: "#030303",
        overlayOpacity: 0.6,
        stagePadding: 6,
        stageRadius: 12,
        popoverOffset: 12,
        popoverClass: "ces-tour",
        showButtons: ["next", "previous"],
        showProgress: false,
        progressText: t("tour.stepOf"),
        nextBtnText: t("tour.next"),
        prevBtnText: t("tour.back"),
        doneBtnText: t("tour.next"),
        skipMissingElement: true,
        onPopoverRender: (popover, { state }) => {
          const step = state.activeStep as DriveStep | undefined;
          const data = step?.data as StepData | undefined;
          const makeExit = () => {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "ces-tour-secondary";
            btn.textContent = t("tour.skip");
            btn.addEventListener("click", () => endDemo("skipped", "/overview"));
            return btn;
          };
          const makeCardSkip = () => {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "ces-tour-secondary";
            btn.textContent = t("tour.skip");
            btn.addEventListener("click", () => {
              markTourSeen();
              endingRef.current = "skipped";
              disposeActive();
            });
            return btn;
          };
          if (isWelcome && data?.secondary?.action === "skip") {
            popover.footerButtons.prepend(makeCardSkip());
          } else if (!isWelcome && !data?.finishDemo) {
            popover.footerButtons.prepend(makeExit());
          }
          // Don't leave a button auto-focused (driver focuses one on render) —
          // the lime focus ring on Skip/Next reads as "click me".
          window.setTimeout(() => {
            const a = document.activeElement as HTMLElement | null;
            if (a && a.closest(".driver-popover")) a.blur();
          }, 0);
        },
        onNextClick: (_elm, step, { driver: drv }) => advance(step, drv),
        onDoneClick: (_elm, step, { driver: drv }) => advance(step, drv),
        onDestroyStarted: (_elm, _step, { driver: drv }) => {
          if (endingRef.current === null) {
            if (isWelcome) {
              // Esc / overlay click on the welcome card = skip.
              markTourSeen();
            } else {
              endingRef.current = "chapter";
              window.setTimeout(() => {
                if (demoStore.active) setTick((n) => n + 1);
              }, 800);
            }
          }
          disposeActive();
          drv.destroy();
        },
      };
      const d = driver(config);
      driverRef.current = d;
      d.drive();
    },
    [senderName, location.pathname, navigate, disposeActive, endDemo, t],
  );

  // Refit the cut-out on any scroll (pages scroll in the window; this also
  // covers late layout as the overview cards animate in).
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (!driverRef.current || frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        driverRef.current?.refresh();
      });
    };
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("scroll", onScroll, true);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  // The welcome card (chapter 0): auto for a fresh user with no projects, or
  // on demand from Settings. Not while the demo is already running.
  useEffect(() => {
    if (!user || demo.active || driverRef.current) return;
    if (!window.matchMedia(DESKTOP).matches) return;
    const forced = forceWelcomeRef.current;
    const onEntry = pathname === "/overview" || pathname === "/onboarding";
    if (!onEntry) return;
    // `?tour=1` replays the demo on demand (QA / showing it off).
    const viaQuery = new URLSearchParams(location.search).get("tour") === "1";
    const show = forced || viaQuery || (!isTourSeen() && projects.length === 0);
    if (!show) return;
    const chapter = DEMO_CHAPTERS[0];
    return whenAnchorReady(chapter, () => {
      if (driverRef.current || demoStore.active) return;
      forceWelcomeRef.current = false;
      run(chapter, 0);
    });
  }, [user, demo.active, pathname, location.search, projects.length, run, tick]);

  // Leaving the welcome card's screen closes it (the demo chapters handle
  // their own route changes below).
  useEffect(() => {
    if (demo.active) return;
    const d = driverRef.current;
    if (!d) return;
    if (pathname !== "/overview" && pathname !== "/onboarding") {
      markTourSeen();
      endingRef.current = "skipped";
      disposeActive();
    }
  }, [pathname, demo.active, disposeActive]);

  // The demo: run the chapter the route calls for.
  useEffect(() => {
    if (!user || !demo.active) return;
    if (!window.matchMedia(DESKTOP).matches) return;
    const match = resolveChapter(DEMO_CHAPTERS, pathname, demo.chapter);
    if (driverRef.current) {
      // Drop the running chapter when the user has left its screen, or its
      // state guard no longer makes it the current chapter (its work is done).
      if (runningPathRef.current !== pathname || match.action !== "run") {
        endingRef.current = "chapter";
        disposeActive();
      } else {
        return;
      }
    }
    const pending = rewindToRef.current;
    if (pending !== null) {
      const target = DEMO_CHAPTERS[pending];
      if (!target || !chapterMatches(target.route, pathname)) return;
      rewindToRef.current = null;
      demoStore.setChapter(pending);
      return;
    }
    if (match.action === "advance" || match.action === "rewind") {
      demoStore.setChapter(match.index);
      return;
    }
    if (match.action !== "run") return;
    const chapter = DEMO_CHAPTERS[match.index];
    return whenAnchorReady({ ...chapter, waitMs: chapter.waitMs ?? Infinity }, () => {
      if (driverRef.current || !demoStore.active) return;
      runningPathRef.current = pathname;
      run(chapter, match.index);
    });
  }, [user, demo.active, demo.chapter, pathname, run, disposeActive, tick, dataTick]);

  const startTour = useCallback(() => {
    if (demoStore.active) demoStore.stop();
    resetTourSeen();
    forceWelcomeRef.current = true;
    if (pathname !== "/overview") navigate("/overview");
    else setTick((n) => n + 1);
  }, [pathname, navigate]);

  const exitDemo = useCallback(() => {
    if (!demoStore.active) return;
    endDemo("skipped", "/overview");
  }, [endDemo]);

  return <TourContext.Provider value={{ startTour, exitDemo }}>{children}</TourContext.Provider>;
}
